import { AUTH_URL } from './config.js';
import { fetchMe, resetForNewSession } from './session.js';
import type { Profile } from './profile.js';
import { BN_AUTH_CHANGE } from './events.js';

const POPUP_POLL_INTERVAL_MS = 500;
const POPUP_FEATURES = 'width=500,height=600';

function isStandalonePwa(): boolean {
  const nav = navigator as unknown as { standalone?: boolean };
  return (
    nav.standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches
  );
}

/**
 * Shared popup/PWA-redirect mechanics behind every popup this package opens
 * (sign-in here, account linking in account.ts): navigates directly on a
 * standalone PWA (`window.open` is unreliable there), otherwise opens
 * `startUrl` in a popup and waits for either a same-origin postMessage or
 * the popup being closed by the user.
 *
 * `handleMessage` receives each message from AUTH_URL's origin and decides
 * whether it is the one being waited for; if so it calls `resolve`,
 * possibly after its own async work first (as openSignIn's handler does for
 * fetchMe()). Any other message is ignored. The returned promise settles
 * exactly once — via `resolve`, or with `null` once the popup is gone and
 * nothing settled it.
 */
export function openAuthPopup<T>(
  startUrl: string,
  handleMessage: (data: Record<string, unknown>, resolve: (value: T | null) => void) => void,
): Promise<T | null> {
  if (isStandalonePwa()) {
    window.location.href = startUrl;
    return Promise.resolve(null);
  }

  const popup = window.open(startUrl, '_blank', POPUP_FEATURES);

  return new Promise<T | null>((resolve) => {
    let resolved = false;
    let pollTimer: ReturnType<typeof setInterval> | null = null;

    const authOrigin = new URL(AUTH_URL).origin;

    function cleanup(): void {
      window.removeEventListener('message', onMessage);
      if (pollTimer !== null) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
    }

    function settle(value: T | null): void {
      if (resolved) {
        return;
      }
      resolved = true;
      cleanup();
      resolve(value);
    }

    function onMessage(event: MessageEvent): void {
      if (event.origin !== authOrigin) {
        return;
      }
      const data = event.data as unknown;
      if (typeof data !== 'object' || data === null) {
        return;
      }
      handleMessage(data as Record<string, unknown>, settle);
    }

    window.addEventListener('message', onMessage);

    pollTimer = setInterval(() => {
      if (popup === null || popup.closed) {
        settle(null);
      }
    }, POPUP_POLL_INTERVAL_MS);
  });
}

export async function openSignIn(
  provider: 'google' | 'github',
): Promise<Profile | null> {
  const startUrl =
    AUTH_URL + '/auth/' + provider + '/start?origin=' + location.origin;

  return openAuthPopup<Profile>(startUrl, (data, resolve) => {
    if (data['success'] !== true) {
      return;
    }

    // The session just changed — a profile request already in flight predates
    // it and would answer for the old (or absent) session.
    resetForNewSession();
    fetchMe()
      .then((profile) => {
        window.dispatchEvent(new CustomEvent(BN_AUTH_CHANGE));
        resolve(profile);
      })
      .catch(() => {
        resolve(null);
      });
  });
}
